import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  FileCheck2, ScrollText, MessageSquare, Download, Calendar, MapPin,
  Check, X, Loader2, Building2, Users, TrendingUp, ExternalLink, Ticket, Award,
  ShieldAlert, ShieldCheck, Shield, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function formatPrettyDate(iso) {
  if (!iso) return '';
  // Parse as local date (avoid timezone off-by-one)
  const parts = iso.split('-').map(Number);
  if (parts.length < 3) return iso;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const day = d.getDate();
  const suffix = (day % 10 === 1 && day !== 11) ? 'st'
    : (day % 10 === 2 && day !== 12) ? 'nd'
    : (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${day}${suffix} ${d.getFullYear()}`;
}

function formatTime12h(t) {
  if (!t || !t.includes(':')) return t || '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmt(cents) {
  return `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

export default function VenueProposalPage() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signOpen, setSignOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/venue-proposals/public/${token}`);
        setP(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Proposal not found' : 'Failed to load proposal');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const refresh = async () => {
    const res = await axios.get(`${API}/venue-proposals/public/${token}`);
    setP(res.data);
  };

  const postComment = async (body, author_name) => {
    const res = await axios.post(`${API}/venue-proposals/public/${token}/comment`, { body, author_name });
    setP({ ...p, comments: [...(p.comments || []), res.data] });
    toast.success('Comment posted');
  };

  const pdfUrl = `${API}/venue-proposals/public/${token}/pdf`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center space-y-3">
        <X className="h-10 w-10 mx-auto text-rose-400" />
        <h2 className="text-xl font-bold">{error}</h2>
        <p className="text-sm text-muted-foreground">The proposal link may have expired or been revoked.</p>
      </CardContent></Card>
    </div>
  );

  const isSigned = p.signature?.signed;
  const isDeclined = p.status === 'declined';

  return (
    <div className="min-h-screen bg-background">
      {/* HERO */}
      <Hero p={p} />

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-8" data-testid="public-venue-proposal">
        <StatusBanner p={p} />

        {/* RFP — prominent Target Attendance + Primary Date & Time box */}
        {p.proposal_type === 'rfp' && (p.target_attendance || (p.proposed_dates || []).length > 0) && (
          <RfpHeadlineBox p={p} />
        )}

        {/* Proposed Dates */}
        {(p.proposed_dates || []).length > 0 && (
          <SectionCard icon={Calendar} title="Proposed Dates">
            <div className="grid sm:grid-cols-2 gap-3">
              {p.proposed_dates.map((d, i) => {
                const pretty = formatPrettyDate(d.date);
                const startT = formatTime12h(d.start_time);
                const endT = formatTime12h(d.end_time);
                const timeStr = startT && endT ? `${startT} – ${endT}` : (startT || '');
                return (
                  <div key={i} className="p-4 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
                    <div className="text-base font-bold font-['Space_Grotesk']">{pretty || d.date}</div>
                    {timeStr && <div className="text-sm text-indigo-300 mt-0.5">{timeStr}</div>}
                    {d.label && !d.start_time && <div className="text-xs text-indigo-300 mt-0.5">{d.label}</div>}
                    {d.description && <div className="text-xs text-muted-foreground mt-1">{d.description}</div>}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Executive Summary */}
        {p.executive_summary && (
          <SectionCard icon={ScrollText} title="Executive Summary">
            <HtmlContent html={p.executive_summary} />
          </SectionCard>
        )}

        {/* Event Overview + Listing Card */}
        {p.event_overview && (
          <SectionCard icon={Calendar} title="Event Overview">
            <HtmlContent html={p.event_overview} />
          </SectionCard>
        )}

        {p.event_card && <EventListingCard card={p.event_card} href={p.event_page_url} />}

        {/* Flyer gallery */}
        {(p.flyer_images || []).length > 0 && (
          <SectionCard icon={ScrollText} title="Event Flyer & Images">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {p.flyer_images.map((src, i) => (
                <img key={i} src={src} alt="" className="w-full rounded-lg object-cover aspect-square" loading="lazy" />
              ))}
            </div>
          </SectionCard>
        )}

        {/* About Eventism + Organizers */}
        <div className="grid md:grid-cols-2 gap-4">
          {p.about_eventism && (
            <SectionCard icon={Building2} title="About Eventism">
              <HtmlContent html={p.about_eventism} />
            </SectionCard>
          )}
          {p.about_organizers && (
            <SectionCard icon={Users} title="Co-Organizers">
              <HtmlContent html={p.about_organizers} />
            </SectionCard>
          )}
        </div>

        {/* Responsibilities */}
        <div className="grid md:grid-cols-2 gap-4">
          <SectionCard icon={Check} title="Eventism Responsibilities" color="text-indigo-400">
            <ul className="text-sm space-y-1.5">
              {(p.eventism_responsibilities || []).map((r, i) => (
                <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-indigo-400 mt-0.5 flex-shrink-0" /><span>{r}</span></li>
              ))}
            </ul>
          </SectionCard>
          <SectionCard icon={Check} title="Venue Responsibilities" color="text-fuchsia-400">
            <ul className="text-sm space-y-1.5">
              {(p.venue_responsibilities || []).map((r, i) => (
                <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-fuchsia-400 mt-0.5 flex-shrink-0" /><span>{r}</span></li>
              ))}
            </ul>
          </SectionCard>
        </div>

        {/* Accommodations — especially for RFP */}
        {(p.accommodations || []).length > 0 && (
          <SectionCard icon={Building2} title={p.proposal_type === 'rfp' ? 'Required Venue Accommodations' : 'Accommodations & House Requirements'} color="text-fuchsia-400">
            <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">
              {p.accommodations.map((a, i) => (
                <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-fuchsia-400 mt-0.5 flex-shrink-0" /><span>{a}</span></li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Media Kit */}
        {p.media_kit && <MediaKitBlock mk={p.media_kit} />}

        {/* Pro Forma */}
        {p.include_proforma && p.proforma_snapshot && <ProformaBlock p={p} />}

        {/* Comments */}
        <PublicCommentsThread comments={p.comments || []} onPost={postComment} defaultName={p.venue_contact_name || p.venue_name || ''} />

        {/* Signature / Decision */}
        <SignatureBlock p={p} onSign={() => setSignOpen(true)} onDecline={() => setDeclineOpen(true)} />

        {/* PDF */}
        <div className="flex justify-center">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="public-pdf-link">
            <Download className="h-4 w-4" /> Download PDF
          </a>
        </div>
      </div>

      {signOpen && <SignDialog token={token} p={p} onClose={(updated) => { setSignOpen(false); if (updated) refresh(); }} />}
      {declineOpen && <DeclineDialog token={token} onClose={(updated) => { setDeclineOpen(false); if (updated) refresh(); }} />}
    </div>
  );
}

function Hero({ p }) {
  const hero = p.hero_image_url || p.media_kit?.hero_image_url || '';
  const typeLabel = p.proposal_type === 'partnership' ? 'Partnership Proposal'
    : p.proposal_type === 'venue_rental' ? 'Venue Rental Proposal'
    : 'Request for Proposal';
  return (
    <div className="relative w-full min-h-[380px] overflow-hidden bg-gradient-to-br from-indigo-900 via-fuchsia-900 to-amber-900">
      {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
      <div className="relative max-w-5xl mx-auto px-4 pt-24 pb-10 flex flex-col justify-end min-h-[380px]">
        <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 w-fit text-xs">{typeLabel}</Badge>
        <h1 className="text-3xl sm:text-5xl font-bold font-['Space_Grotesk'] mt-3" data-testid="proposal-hero-title">{p.title}</h1>
        {p.venue_name && (
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {p.venue_logo_url && (
              <img src={p.venue_logo_url} alt={`${p.venue_name} logo`} className="h-10 w-10 rounded bg-white/90 object-contain p-1" />
            )}
            <div>
              <div className="text-sm sm:text-base">
                For <strong className="text-foreground">{p.venue_name}</strong>
              </div>
              {p.venue_address && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3" />{p.venue_address}
                </div>
              )}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">Regarding · {p.event_title}</p>
      </div>
    </div>
  );
}

function StatusBanner({ p }) {
  if (p.signature?.signed) return (
    <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
      <FileCheck2 className="h-5 w-5 text-emerald-400" />
      <div>
        <div className="font-semibold text-emerald-300">Agreement signed</div>
        <div className="text-xs text-muted-foreground">Signed by {p.signature.signer_name} on {new Date(p.signature.signed_at).toLocaleString()}.</div>
      </div>
    </div>
  );
  if (p.status === 'declined') return (
    <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
      <div className="font-semibold text-rose-300">Proposal declined</div>
      {p.decline_reason && <div className="text-xs text-muted-foreground mt-1">Reason: {p.decline_reason}</div>}
    </div>
  );
  return null;
}

function SectionCard({ icon: Icon, title, color, children }) {
  return (
    <Card className="border-border">
      <CardContent className="p-5 space-y-3">
        <h2 className="text-lg font-semibold flex items-center gap-2 font-['Space_Grotesk']">
          <Icon className={`h-5 w-5 ${color || 'text-indigo-400'}`} />
          {title}
        </h2>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * HtmlContent — renders HTML from the admin rich-text editor.
 * Uses Tailwind's prose utilities for attractive default typography.
 * If the incoming value looks like plain text (no HTML tags), it's rendered
 * as a whitespace-preserved paragraph instead.
 */
function HtmlContent({ html }) {
  if (!html) return null;
  const hasTags = /<[a-z][^>]*>/i.test(html);
  if (!hasTags) {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap">{html}</p>;
  }
  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none prose-a:text-indigo-400 prose-headings:font-['Space_Grotesk']"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * RfpHeadlineBox — prominent target-attendance + primary date/time card for RFPs.
 * Shown at the top of the body so venues see scale, date, and time at a glance.
 */
function RfpHeadlineBox({ p }) {
  const primary = (p.proposed_dates || [])[0];
  const pretty = primary ? formatPrettyDate(primary.date) : '';
  const startT = primary ? formatTime12h(primary.start_time) : '';
  const endT = primary ? formatTime12h(primary.end_time) : '';
  const timeStr = startT && endT ? `${startT} – ${endT}` : (startT || '');

  return (
    <div className="rounded-2xl border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/10 via-indigo-500/5 to-transparent p-6 sm:p-8" data-testid="rfp-headline-box">
      <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300 font-semibold mb-3">
        Request for Proposal
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Target Attendance</div>
          <div className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] text-fuchsia-300 leading-tight">
            {p.target_attendance || '—'}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Date {timeStr ? '& Time' : ''}</div>
          {primary ? (
            <>
              <div className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] leading-tight">
                {pretty || primary.date}
              </div>
              {timeStr && (
                <div className="text-base text-indigo-300 mt-1 flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />{timeStr}
                </div>
              )}
              {primary.label && <div className="text-xs text-muted-foreground mt-1">{primary.label}</div>}
            </>
          ) : (
            <div className="text-base text-muted-foreground">Dates flexible</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MediaKitBlock({ mk }) {
  const rows = [
    ['Expected attendance', mk.expected_attendance],
    ['Estimated reach', mk.estimated_reach],
    ['Demographics', mk.demographics],
    ['Age range', mk.age_range],
    ['Social reach', mk.social_reach],
  ].filter(([, v]) => v);
  if (rows.length === 0 && !(mk.gallery_images || []).length) return null;
  return (
    <SectionCard icon={TrendingUp} title="Media Kit" color="text-amber-400">
      {rows.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-border/50 py-1.5">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium text-right">{v}</span>
            </div>
          ))}
        </div>
      )}
      {mk.extra_notes && <p className="text-sm mt-3 text-muted-foreground whitespace-pre-wrap">{mk.extra_notes}</p>}
      {(mk.gallery_images || []).length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-4">
          {mk.gallery_images.slice(0, 8).map((src, i) => (
            <img key={i} src={src} alt="" className="aspect-square object-cover rounded-md" loading="lazy" />
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function EventListingCard({ card, href }) {
  const prettyDate = formatPrettyDate(card.event_date);
  const time = formatTime12h(card.event_time);
  const priceRange = (card.min_price && card.max_price && card.min_price !== card.max_price)
    ? `$${card.min_price.toFixed(0)} – $${card.max_price.toFixed(0)}`
    : (card.min_price ? `From $${card.min_price.toFixed(0)}` : null);
  return (
    <Card className="border-indigo-500/30 hover:border-indigo-500/60 transition-colors overflow-hidden" data-testid="event-listing-card">
      <a href={href} target="_blank" rel="noopener noreferrer" className="block group">
        <div className="grid md:grid-cols-[240px_1fr]">
          <div className="aspect-video md:aspect-auto md:h-full bg-muted relative overflow-hidden">
            {card.image_url
              ? <img src={card.image_url} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Calendar className="h-8 w-8" /></div>}
          </div>
          <div className="p-5 space-y-2">
            <div className="flex items-center gap-2 text-xs text-indigo-400 font-semibold uppercase tracking-wide">
              <Calendar className="h-3.5 w-3.5" /> Live Event Listing
            </div>
            <h3 className="text-xl font-bold font-['Space_Grotesk']">{card.title}</h3>
            {card.short_description && <p className="text-sm text-muted-foreground line-clamp-2">{card.short_description}</p>}
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap pt-1">
              {prettyDate && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{prettyDate}{time ? ` · ${time}` : ''}</span>}
              {card.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{card.city}</span>}
              {priceRange && <span className="flex items-center gap-1"><Ticket className="h-3 w-3" />{priceRange}</span>}
            </div>
            <div className="pt-2">
              <span className="inline-flex items-center gap-1 text-sm text-indigo-400 font-semibold">
                View live event page <ExternalLink className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </div>
      </a>
    </Card>
  );
}

function ProformaBlock({ p }) {
  const snap = p.proforma_snapshot || {};
  const target = snap.target_attendance || 0;
  const breakeven = snap.breakeven_cents || 0;
  const ticketsToBE = snap.tickets_to_breakeven || 0;
  const venueTake = snap.venue_take_cents || 0;
  const venuePerTicket = snap.venue_per_ticket_cents || 0;
  const rows = snap.forecast_rows || [];
  const totalIncome = snap.forecast_total_cents || snap.estimated_income_cents || 0;
  const cacCents = snap.cac_cents || 0;
  const cacRatio = snap.cac_ratio_pct || 0;
  const riskLevel = snap.risk_level || 'unknown';
  const riskLabel = snap.risk_label || 'Risk —';
  const riskNote = snap.risk_note || '';

  const riskTone = {
    low: { card: 'text-emerald-400', Icon: ShieldCheck },
    moderate: { card: 'text-amber-400', Icon: Shield },
    elevated: { card: 'text-orange-400', Icon: Shield },
    high: { card: 'text-rose-400', Icon: ShieldAlert },
    unknown: { card: 'text-muted-foreground', Icon: Shield },
  }[riskLevel];
  const RiskIcon = riskTone.Icon;

  return (
    <div className="space-y-4" data-testid="proforma-block">
      {/* Target attendance + breakeven + CAC/risk cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        <StatCard icon={Users} label="Target Attendance" value={target > 0 ? `${target.toLocaleString()}` : '—'} sub="forecast from ticket plan" color="text-amber-400" />
        <StatCard icon={TrendingUp} label="Breakeven" value={fmt(breakeven)} sub={ticketsToBE > 0 ? `${ticketsToBE} tickets to breakeven` : 'Set a forecast'} color="text-indigo-400" />
        <StatCard
          icon={RiskIcon}
          label="CAC · Cost to Acquire"
          value={cacCents > 0 ? fmt(cacCents) : '—'}
          sub={cacCents > 0 ? `${riskLabel}${cacRatio ? ` · ${cacRatio}% of avg. ticket` : ''}` : 'No marketing budget tagged'}
          color={riskTone.card}
        />
      </div>

      {/* Risk tolerance explainer */}
      {cacCents > 0 && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 ${
          riskLevel === 'low' ? 'border-emerald-500/30 bg-emerald-500/5' :
          riskLevel === 'moderate' ? 'border-amber-500/30 bg-amber-500/5' :
          riskLevel === 'elevated' ? 'border-orange-500/30 bg-orange-500/5' :
          riskLevel === 'high' ? 'border-rose-500/30 bg-rose-500/5' :
          'border-border bg-muted/20'
        }`} data-testid="risk-tolerance-note">
          <RiskIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${riskTone.card}`} />
          <div className="text-sm">
            <div className="font-semibold">Risk tolerance: {riskLabel}</div>
            <div className="text-muted-foreground mt-0.5">{riskNote}</div>
          </div>
        </div>
      )}

      {/* Venue compensation — primary value for the venue, no revenue-split framing */}
      <SectionCard icon={Award} title="Venue Compensation" color="text-fuchsia-400">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="p-5 rounded-lg bg-fuchsia-500/5 border border-fuchsia-500/30">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Venue receives (estimate)</div>
            <div className="text-3xl font-bold text-fuchsia-400 mt-2 tabular-nums">{fmt(venueTake)}</div>
          </div>
          <div className="p-5 rounded-lg bg-fuchsia-500/5 border border-fuchsia-500/30">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Per ticket value</div>
            <div className="text-3xl font-bold text-fuchsia-400 mt-2 tabular-nums">
              {venuePerTicket > 0 ? `${fmt(venuePerTicket)} per ticket` : '—'}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground pt-1">
          {p.proposal_type === 'venue_rental'
            ? 'Based on the agreed rental fee, spread across the forecast attendance.'
            : 'Based on the forecast ticket plan below. Scales with actual sales.'}
        </p>
      </SectionCard>

      {/* Income forecast — matches total exactly */}
      {rows.length > 0 && (
        <SectionCard icon={Ticket} title="Income Forecast">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="py-2 pr-3">Line</th>
                  <th className="py-2 px-3 text-right">Price</th>
                  <th className="py-2 px-3 text-right">Qty</th>
                  <th className="py-2 pl-3 text-right">Estimated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{r.name}</span>
                      {r.kind === 'sponsorship' && <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-300">sponsorship</span>}
                      {r.kind === 'other_income' && <span className="ml-2 text-[10px] uppercase tracking-wide text-indigo-300">other income</span>}
                      {r.notes && <div className="text-[11px] text-muted-foreground mt-0.5">{r.notes}</div>}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{r.price_cents != null ? fmt(r.price_cents) : '—'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{r.qty != null ? r.qty.toLocaleString() : '—'}</td>
                    <td className="py-2 pl-3 text-right tabular-nums font-semibold">{fmt(r.total_cents)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-indigo-500/40 bg-indigo-500/5">
                  <td className="py-2 pr-3 font-bold text-indigo-400" colSpan={3}>Total forecast income</td>
                  <td className="py-2 pl-3 text-right tabular-nums font-bold text-indigo-400">{fmt(totalIncome)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Expense breakdown with notes */}
      {(snap.expense_lines || []).length > 0 && (
        <SectionCard icon={TrendingUp} title="Cost Line-Items (Full Breakdown)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 px-3">Line Item</th>
                  <th className="py-2 pl-3 text-right">Estimated</th>
                </tr>
              </thead>
              <tbody>
                {snap.expense_lines.map((ln, i) => (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="py-2 pr-3 text-xs uppercase text-muted-foreground">{ln.category}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{ln.name}</div>
                      {ln.notes && <div className="text-[11px] text-muted-foreground whitespace-pre-wrap mt-0.5">{ln.notes}</div>}
                    </td>
                    <td className="py-2 pl-3 text-right tabular-nums">{fmt(ln.estimated_cents)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-rose-500/40 bg-rose-500/5">
                  <td className="py-2 pr-3 font-bold text-rose-400" colSpan={2}>Total Estimated Expenses</td>
                  <td className="py-2 pl-3 text-right tabular-nums font-bold text-rose-400">{fmt(snap.estimated_expense_cents || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Sponsorship plan */}
      <SponsorshipBlock snap={snap} eventPageUrl={p.event_page_url} eventCity={p.event_city} />

      <p className="text-[11px] text-muted-foreground text-center">
        Pro forma snapshot as of {snap.snapshot_at ? new Date(snap.snapshot_at).toLocaleString() : '—'}. Figures are forecasts and will update as real sales come in.
      </p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4 space-y-1">
        <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${color || 'text-indigo-400'}`}>
          <Icon className="h-4 w-4" /> {label}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SponsorshipBlock({ snap, eventPageUrl, eventCity }) {
  const tiers = snap.sponsorship_tiers || [];
  const committed = snap.committed_sponsors || [];
  if (tiers.length === 0 && committed.length === 0) return null;
  // Link to the event page's sponsorship section
  const sponsorshipLink = eventPageUrl ? `${eventPageUrl}#sponsorship` : null;
  return (
    <SectionCard icon={Award} title="Sponsorship Plan" color="text-amber-400">
      {tiers.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Available Tiers</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {tiers.map((t) => (
              <div key={t.tier_id} className="p-3 rounded border border-border flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{t.name}</div>
                  {t.includes_swag_bag && <div className="text-[10px] text-amber-300 uppercase">+ Swag bag</div>}
                </div>
                <div className="text-right">
                  <div className="font-semibold tabular-nums">{fmt(t.price_cents)}</div>
                  {t.quantity_total > 0 && <div className="text-[10px] text-muted-foreground">× {t.quantity_total} avail</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {committed.length > 0 && (
        <div className="space-y-2 pt-3 border-t border-border">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Confirmed Sponsors</div>
          <div className="flex flex-wrap gap-3">
            {committed.map((s) => (
              <div key={s.sponsorship_id} className="flex items-center gap-2 p-2 rounded border border-border bg-muted/30">
                {s.logo_url && <img src={s.logo_url} alt="" className="h-8 w-8 rounded bg-white/90 object-contain p-0.5" />}
                <div className="text-sm">
                  <div className="font-semibold">{s.sponsor_company || s.sponsor_name || 'Sponsor'}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">{s.tier_name || s.tier}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {sponsorshipLink && (
        <a href={sponsorshipLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-amber-400 hover:underline pt-2">
          View sponsorship page <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </SectionCard>
  );
}

function PublicCommentsThread({ comments, onPost, defaultName }) {
  const [name, setName] = useState(defaultName || '');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim() || !name.trim()) {
      toast.error('Please enter your name and comment');
      return;
    }
    setBusy(true);
    try { await onPost(body.trim(), name.trim()); setBody(''); }
    finally { setBusy(false); }
  };
  return (
    <SectionCard icon={MessageSquare} title={`Questions, comments & concerns (${comments.length})`}>
      {comments.length > 0 && (
        <div className="space-y-2">
          {comments.map((c) => (
            <div key={c.id} className={`p-3 rounded-md text-sm ${c.author_type === 'admin' ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-fuchsia-500/5 border border-fuchsia-500/20'}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold">{c.author_name} <span className="text-[10px] text-muted-foreground uppercase ml-1">{c.author_type === 'admin' ? 'Eventism' : 'Venue'}</span></span>
                <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2 pt-3 border-t border-border">
        <div className="grid sm:grid-cols-3 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="public-comment-name" />
          <div className="sm:col-span-2">
            <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share a question or concern…" data-testid="public-comment-body" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="public-comment-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MessageSquare className="h-4 w-4 mr-1" />}
            Post comment
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

function SignatureBlock({ p, onSign, onDecline }) {
  if (p.signature?.signed) {
    const sig = p.signature;
    return (
      <Card className="border-emerald-500/30 bg-emerald-500/5">
        <CardContent className="p-5 space-y-2 text-center">
          <FileCheck2 className="h-10 w-10 mx-auto text-emerald-400" />
          <h2 className="text-xl font-bold">Agreement signed</h2>
          <p className="text-sm">
            <em className="text-lg font-['Dancing_Script','cursive'] text-foreground">{sig.typed_signature}</em>
          </p>
          <p className="text-xs text-muted-foreground">
            {sig.signer_name} · {sig.signer_title || '—'} · {new Date(sig.signed_at).toLocaleString()}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (p.status === 'declined') return null;
  return (
    <Card className="border-fuchsia-500/30">
      <CardContent className="p-5 space-y-3 text-center">
        <h2 className="text-xl font-bold">Ready to partner?</h2>
        <p className="text-sm text-muted-foreground">Type your name to sign and approve this proposal, or decline with a reason.</p>
        <div className="flex gap-2 justify-center">
          <Button onClick={onSign} className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-sign-dialog">
            <FileCheck2 className="h-4 w-4 mr-1" /> Sign & Approve
          </Button>
          <Button variant="outline" onClick={onDecline} data-testid="open-decline-dialog">
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SignDialog({ token, p, onClose }) {
  const [form, setForm] = useState({
    signer_name: p.venue_contact_name || '',
    signer_title: '',
    typed_signature: '',
    agreed_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.signer_name || !form.typed_signature || !form.agreed_terms) {
      toast.error('Please complete all fields and agree to the terms');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/venue-proposals/public/${token}/sign`, { ...form, decision: 'approve' });
      toast.success('Thank you — agreement signed!');
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-card max-w-md">
        <DialogHeader><DialogTitle>Sign & Approve</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Full Name *</Label>
              <Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} data-testid="sign-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })} placeholder="GM, Owner, etc." />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Typed Signature *</Label>
            <Input value={form.typed_signature} onChange={(e) => setForm({ ...form, typed_signature: e.target.value })}
              placeholder="Type your name"
              className="font-['Dancing_Script','cursive'] text-xl h-14"
              data-testid="sign-signature" />
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <Checkbox checked={form.agreed_terms} onCheckedChange={(v) => setForm({ ...form, agreed_terms: !!v })} data-testid="sign-agree" />
            <span>I am authorized to sign on behalf of the venue and I agree to the terms outlined in this proposal. I understand that typing my name constitutes a legally binding electronic signature.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-sign">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileCheck2 className="h-4 w-4 mr-1" />}
            Sign & Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ token, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/venue-proposals/public/${token}/sign`, { decision: 'decline', decline_reason: reason });
      toast.success('Feedback sent');
      onClose(true);
    } catch (e) {
      toast.error('Failed to submit');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-card max-w-md">
        <DialogHeader><DialogTitle>Decline Proposal</DialogTitle></DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-xs">Let us know why (optional)</Label>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Scheduling conflict, budget fit, etc." data-testid="decline-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="submit-decline">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Decline proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
